// Montagem e validação do PEDIDO (checkout) — lógica pura e testável.
// Traduz a sacola do cliente no corpo do POST, SEM enviar preço (o backend
// reprecifica). Validação de contato (nome + WhatsApp) espelha a regra do backend.

import { clampQty, type BagItem } from './bag';
import type { PedidoEndereco, PedidoItemPayload, PedidoPayload, RuaEntrega, TipoEntrega, ZonaEntrega } from './api';
import { chaveLogradouro } from './chaveLogradouro';
import { isPhoneBR } from './masks';
import { formatBRL } from './format';

export type Payment = 'PIX' | 'CARTAO' | 'ENTREGA';

/**
 * Como a loja cobra a entrega, nas MESMAS regras do preview do checkout (rua → zona → taxa padrão). `null` = a loja
 * não cobra nada pelo cardápio ("a combinar"). QA 31/10/2026: os textos diziam "Frete a combinar" e "Entrega
 * combinada no atendimento" numa loja com taxa padrão, e o resumo logo abaixo cobrava a taxa.
 */
export function regraDoFrete(
  zonas: readonly unknown[] | null | undefined,
  ruas: readonly unknown[] | null | undefined,
  taxaPadrao: number | null | undefined
): string | null {
  if ((zonas?.length ?? 0) > 0) return 'Taxa pelo seu bairro';
  const temPadrao = taxaPadrao != null && taxaPadrao > 0;
  if ((ruas?.length ?? 0) > 0) return 'Taxa pelo seu endereço';
  if (temPadrao) return `Taxa de entrega ${formatBRL(taxaPadrao)}`;
  return null;
}

export const PAYMENT_LABEL: Record<Payment, string> = {
  PIX: 'Pix',
  CARTAO: 'Cartão de crédito',
  ENTREGA: 'Pagar na entrega/retirada',
};

/** Gera um idempotencyKey; usa crypto.randomUUID quando disponível. */
export function newIdempotencyKey(): string {
  const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  // Fallback determinístico o suficiente para o browser (não é segredo criptográfico).
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Sacola → itens do pedido (só ids + quantidade; nunca preço). */
export function bagToItens(bag: BagItem[]): PedidoItemPayload[] {
  return bag.map((i) => {
    const item: PedidoItemPayload = {
      produtoId: i.produto.produtoId,
      // Defesa em profundidade (QA ALTO): clampa de novo aqui mesmo que useBag/itemValido já devessem
      // ter normalizado ao reidratar — este é o ÚLTIMO ponto antes do payload sair pro backend, então
      // nunca deixa uma quantidade fora de [1, MAX_QTY] ou não-inteira vazar pro pedido.
      quantidade: clampQty(i.qty),
      adicionalIds: i.adicionais.map((a) => a.id),
    };
    if (i.variacao) item.variacaoId = i.variacao.id;
    if (i.montagemPizza) item.montagemPizza = i.montagemPizza;
    // Observação por item (QA MÉDIO): trim + omite se vazia/whitespace.
    const obs = i.observacao?.trim();
    if (obs) item.observacao = obs;
    return item;
  });
}

/** Achata o endereço do formulário numa string única (só campos preenchidos): p.ex.
 *  "Rua X, 100, Centro, Bauru · SP, ap 2 (CEP 17000-000)". Vazio → string vazia. */
/**
 * A cidade SEM o sufixo " · UF" que versões anteriores guardavam no campo (QA 31/10/2026): com ele, a rua cadastrada com
 * cidade nunca casava no preview do frete, e a cidade não ia ao servidor.
 */
export function cidadePura(cidade: string | null | undefined): string {
  return (cidade ?? '').replace(/\s*·\s*[A-Za-z]{2}\s*$/, '').trim();
}

export function flattenEndereco(e: PedidoEndereco): string {
  // O NÚMERO entra logo depois do logradouro ("Rua X, 100, Centro"): o `endereco` achatado é o texto que sai
  // no cupom e na rota do entregador — sem o número nessa string, quem entrega perde a casa.
  // A UF do próprio campo; num endereço salvo por versão anterior, a do sufixo " · UF" da cidade.
  const uf = ((e.uf ?? '').trim() || (/·\s*([A-Za-z]{2})\s*$/.exec(e.cidade ?? '')?.[1] ?? '')).toUpperCase();
  const cidade = cidadePura(e.cidade);
  const partes = [e.logradouro, e.numero, e.bairro, cidade && uf ? `${cidade} · ${uf}` : cidade, e.complemento]
    .map((p) => (p ?? '').trim())
    .filter((p) => p.length > 0);
  let s = partes.join(', ');
  const cep = (e.cep ?? '').trim();
  if (cep) s = s ? `${s} (CEP ${cep})` : `CEP ${cep}`;
  return s;
}

/** Contato válido: nome não-vazio + WhatsApp BR válido (regra do backend). */
export function contatoValido(nome: string, whatsapp: string): boolean {
  return (nome ?? '').trim().length > 0 && isPhoneBR(whatsapp ?? '');
}

/**
 * Normaliza o bairro para casar com a zona — MESMA regra do backend (trim + minúsculas, sem dobra de acento),
 * para o preview do checkout bater com o cálculo autoritativo do servidor.
 */
export function normalizeBairro(v: string | null | undefined): string {
  return (v ?? '').trim().toLowerCase();
}

/**
 * Acha a zona de entrega ATIVA do bairro informado (case-insensitive), ou null. Fase 1.1: casamento por
 * BAIRRO (cidade reservada para futuro). Usado só para o PREVIEW da taxa no checkout — a autoridade é o backend.
 */
export function zonaDoBairro(zonas: ZonaEntrega[], bairro: string | null | undefined): ZonaEntrega | null {
  const b = normalizeBairro(bairro);
  if (!b) return null;
  return zonas.find((z) => normalizeBairro(z.bairro) === b) ?? null;
}

/**
 * A rua com taxa própria do endereço (21/10/2026), pela CHAVE (a mesma do servidor) e pela cidade (sem cidade na rua
 * ou no endereço, vale). Entre duas, a da cidade do endereço vence a "qualquer cidade". Só PREVIEW — o servidor
 * recalcula na submissão.
 */
export function ruaDoEndereco(
  ruas: RuaEntrega[],
  logradouro: string | null | undefined,
  cidade: string | null | undefined,
): RuaEntrega | null {
  const chave = chaveLogradouro(logradouro);
  if (!chave) return null;
  const cidadeChave = chaveLogradouro(cidadePura(cidade));
  const candidatas = ruas.filter(
    (r) => r.chave === chave && (r.cidade == null || !cidadeChave || chaveLogradouro(r.cidade) === cidadeChave),
  );
  return candidatas.find((r) => r.cidade != null) ?? candidatas[0] ?? null;
}

export interface BuildPedidoInput {
  bag: BagItem[];
  clienteNome: string;
  clienteContato: string;
  tipoEntrega: TipoEntrega;
  endereco?: PedidoEndereco;
  formaPagamento: Payment;
  observacoes?: string;
  idempotencyKey?: string;
  // AGENDAMENTO (Fase 2.1): instante ISO da janela escolhida; ausente = "o quanto antes".
  agendadoPara?: string;
  // Cliente EMPRESA (PJ): CNPJ (pode ser alfanumérico). Preenchido ⇒ cadastra como pessoa jurídica.
  cnpj?: string;
}

/** Monta o corpo do POST /pedidos. PREÇO nunca entra. */
export function buildPedidoPayload(input: BuildPedidoInput): PedidoPayload {
  const payload: PedidoPayload = {
    idempotencyKey: input.idempotencyKey ?? newIdempotencyKey(),
    clienteNome: (input.clienteNome ?? '').trim(),
    clienteContato: (input.clienteContato ?? '').trim(),
    tipoEntrega: input.tipoEntrega,
    formaPagamento: PAYMENT_LABEL[input.formaPagamento],
    itens: bagToItens(input.bag),
  };
  if (input.tipoEntrega === 'ENTREGA' && input.endereco) {
    // Backend guarda `endereco` como TEXTO — achata os campos preenchidos numa linha só.
    const s = flattenEndereco(input.endereco);
    if (s) payload.endereco = s;
    // Bairro ESTRUTURADO (Fase 1.1): o backend acha a ZONA e calcula o frete.
    const bairro = (input.endereco.bairro ?? '').trim();
    if (bairro) payload.bairro = bairro;
    // CIDADE (QA 31/10/2026): sem ela o servidor casava a rua com taxa própria de QUALQUER cidade e cobrava um frete
    // diferente do que a tela mostrou; e o endereço entrava no cadastro sem cidade.
    const cidade = cidadePura(input.endereco.cidade);
    if (cidade) payload.cidade = cidade.slice(0, 80);
    // Endereço ESTRUTURADO (10/09): cep/numero/uf vão TAMBÉM em campos próprios — é com eles que o backend
    // grava o endereço no cadastro e reconhece o mesmo local no próximo pedido (identidade = CEP+número).
    // Enviados só quando preenchidos (são opcionais no contrato) e recortados nos limites do servidor
    // (@Size 9/20/2), para o cliente nunca levar um 400 por um caractere a mais.
    const cep = (input.endereco.cep ?? '').trim();
    if (cep) payload.cep = cep.slice(0, 9);
    // Logradouro SEM o número (o campo `endereco` achatado já leva os dois juntos para o cupom/rota).
    const logradouro = (input.endereco.logradouro ?? '').trim();
    if (logradouro) payload.logradouro = logradouro.slice(0, 200);
    const numero = (input.endereco.numero ?? '').trim();
    if (numero) payload.numero = numero.slice(0, 20);
    const uf = (input.endereco.uf ?? '').trim().toUpperCase();
    if (uf) payload.uf = uf.slice(0, 2);
  }
  const obs = (input.observacoes ?? '').trim();
  if (obs) payload.observacoes = obs;
  // Agendamento (Fase 2.1): só vai quando o cliente escolheu um horário; ausente = imediato.
  if (input.agendadoPara) payload.agendadoPara = input.agendadoPara;
  // Cliente EMPRESA: CNPJ preenchido ⇒ manda personType=PJ + documento; o backend valida o dígito e
  // cadastra/atualiza como pessoa jurídica. Sem CNPJ ⇒ pessoa física (fluxo atual).
  const cnpj = (input.cnpj ?? '').trim();
  if (cnpj) {
    payload.personType = 'PESSOA_JURIDICA';
    payload.documento = cnpj;
  }
  return payload;
}
